import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import universalDataService from '../services/universalDataService';
import { ConfirmPanel, Field, Icon, ICON, Panel, formatDate } from './ui';
import {
  EQUIPMENT_TYPES, buildEquipment, buildRepair, calcAnnualCost, emptyEquipment, emptyRepair, formFromEquipment, formFromRepair,
  fuelForEquipment, getEqCostBreakdown, normalizeFuel, repairPerYear, repairYearsText,
} from './fuelLogic';
import './Motorina.css';

// The hidden Costuri page reads this formula from here.
export { getEqCostBreakdown } from './fuelLogic';

const lei = (value) => `${Math.round(value || 0).toLocaleString('ro-RO')} lei`;
const plainNumber = (value) => Math.round(value || 0).toLocaleString('ro-RO');
const litersText = (value) => `${(Number(value) || 0).toLocaleString('ro-RO', { maximumFractionDigits: 1 })} L`;
const decimalText = (value) => value.replace(',', '.').replace(/[^0-9.]/g, '');
const wholeText = (value) => value.replace(/[^0-9]/g, '');
const CURRENT_YEAR = new Date().getFullYear();
// Reaches back to the oldest machine, so its first years can be looked at too.
const yearsFor = (machines) => {
  const starts = machines.map((eq) => Number(eq.acquisitionType === 'leasing' ? eq.leasingStartYear : eq.purchaseYear)).filter((year) => year > 1900);
  const first = Math.min(CURRENT_YEAR - 4, ...starts);
  return Array.from({ length: CURRENT_YEAR + 15 - first + 1 }, (_, index) => first + index);
};

const acquisitionText = (eq) => (eq.acquisitionType === 'leasing' ? 'Leasing' : 'Achiziție');

const costExplanation = (eq) => {
  const { totalAchizitie, valRevanzare, costAnual, aniPastrare } = getEqCostBreakdown(eq);
  const start = eq.acquisitionType === 'leasing'
    ? `Avans ${plainNumber(eq.avans)} lei + ${plainNumber(eq.annualInstallment)} lei/an × ${eq.leasingYears} ani`
    : `Achiziție ${plainNumber(totalAchizitie)} lei`;
  return `${start}${valRevanzare > 0 ? ` − revânzare ${plainNumber(valRevanzare)} lei` : ''} = ${plainNumber(costAnual)} lei/an, pe ${aniPastrare} ani`;
};

// ---------- Forms ----------

const EquipmentPanel = ({ equipment, onSave, onClose }) => {
  const [form, setForm] = useState(() => (equipment ? formFromEquipment(equipment) : emptyEquipment()));
  const [tried, setTried] = useState(false);
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const money = (key, label, placeholder) => (
    <Field label={label}><input inputMode="decimal" placeholder={placeholder} value={form[key]} onChange={(e) => set({ [key]: decimalText(e.target.value) })} /></Field>
  );
  const whole = (key, label, placeholder) => (
    <Field label={label}><input inputMode="numeric" placeholder={placeholder} value={form[key]} onChange={(e) => set({ [key]: wholeText(e.target.value) })} /></Field>
  );

  const preview = getEqCostBreakdown(buildEquipment(form, equipment, 0));
  const submit = (event) => {
    event.preventDefault();
    setTried(true);
    if (form.name.trim()) onSave({ ...form, name: form.name.trim() });
  };

  return (
    <Panel title={equipment ? 'Modifică utilajul' : 'Utilaj nou'} onClose={onClose} onSubmit={submit}
      footer={(
        <>
          <button type="button" className="pm-btn-soft pm-only-wide" onClick={onClose}>Anulează</button>
          <button type="submit" className="pm-btn-primary">Salvează utilajul</button>
        </>
      )}>
      <div>
        <p className="mo-label">Tip</p>
        <div className="eq-types" role="group" aria-label="Tip">
          {[...new Set([...EQUIPMENT_TYPES, form.type])].map((type) => (
            <button key={type} type="button" className={form.type === type ? 'is-on' : ''} aria-pressed={form.type === type} onClick={() => set({ type })}>{type}</button>
          ))}
        </div>
      </div>
      <Field label="Nume" error={tried && !form.name.trim()}>
        <input placeholder="ex: Tractor 1" value={form.name} onChange={(e) => set({ name: e.target.value })} />
      </Field>
      <div className="eq-three">
        <Field label="Marcă"><input value={form.brand} onChange={(e) => set({ brand: e.target.value })} /></Field>
        <Field label="Model"><input value={form.model} onChange={(e) => set({ model: e.target.value })} /></Field>
        {whole('year', 'An')}
      </div>
      <div className="pm-segments eq-segments st-two" role="group" aria-label="Cum a fost cumpărat">
        {[['cash', 'Achiziție'], ['leasing', 'Leasing']].map(([id, label]) => (
          <button key={id} type="button" className={form.acquisitionType === id ? 'is-on' : ''} aria-pressed={form.acquisitionType === id} onClick={() => set({ acquisitionType: id })}>{label}</button>
        ))}
      </div>
      {form.acquisitionType === 'cash' ? (
        <>
          <div className="pm-two">
            {money('purchasePrice', 'Preț (lei)', 'ex: 150000')}
            {whole('purchaseYear', 'An achiziție')}
          </div>
          {whole('amortizationYears', 'Ani de amortizare')}
        </>
      ) : (
        <>
          <div className="pm-two">
            {money('avans', 'Avans (lei)', 'ex: 40000')}
            {money('annualInstallment', 'Rată anuală (lei)', 'ex: 32000')}
          </div>
          <div className="pm-two">
            {whole('leasingYears', 'Ani de leasing')}
            {whole('leasingStartYear', 'An început')}
          </div>
        </>
      )}
      <div className="pm-two">
        {money('valoareRevanzare', 'Val. revânzare (lei)', 'Cât estimezi')}
        {whole('aniPastrare', 'Ani păstrare', form.acquisitionType === 'leasing' ? form.leasingYears || '5' : form.amortizationYears || '10')}
      </div>
      {preview.totalAchizitie > 0 && (
        <div className="mo-tip">
          Cost net {lei(preview.costAnual)}/an, pe {preview.aniPastrare} ani.
          {preview.valBlocataAnual > 0 && ` Pe lângă el, ${lei(preview.valBlocataAnual)}/an rămân blocați în utilaj și se recuperează la vânzare.`}
        </div>
      )}
      <Field label="Note"><input placeholder="Opțional" value={form.notes} onChange={(e) => set({ notes: e.target.value })} /></Field>
    </Panel>
  );
};

const RepairPanel = ({ equipment, repair, onSave, onDelete, onClose }) => {
  const [form, setForm] = useState(() => (repair ? formFromRepair(repair) : emptyRepair()));
  const [tried, setTried] = useState(false);
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const multi = form.amortizationType === 'multi';
  const years = parseInt(form.amortizationYears, 10) || 2;
  const cost = parseFloat(form.cost) || 0;

  const submit = (event) => {
    event.preventDefault();
    setTried(true);
    if (cost > 0 && form.date) onSave(form);
  };

  return (
    <Panel title={repair ? 'Modifică costul' : 'Cost de mentenanță'} subtitle={equipment.name} onClose={onClose} onSubmit={submit}
      footer={(
        <>
          {repair && <button type="button" className="pm-btn-soft pm-danger" onClick={onDelete}>Șterge</button>}
          <button type="submit" className="pm-btn-accent">Salvează costul</button>
        </>
      )}>
      <div className="mo-big">
        <Field label="Cost (lei)" error={tried && cost <= 0}><input inputMode="decimal" placeholder="5000" value={form.cost} onChange={(e) => set({ cost: decimalText(e.target.value) })} /></Field>
      </div>
      <Field label="Ce s-a făcut"><input placeholder="ex: Revizie motor" value={form.description} onChange={(e) => set({ description: e.target.value })} /></Field>
      <Field label="Data" error={tried && !form.date}><input type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} /></Field>
      <div>
        <p className="mo-label">Cum se împarte costul</p>
        <div className="mo-choice st-one" role="group" aria-label="Cum se împarte costul">
          <button type="button" className={!multi ? 'is-on' : ''} aria-pressed={!multi} onClick={() => set({ amortizationType: 'current' })}>
            <strong>Tot într-un an</strong><span>Pentru cheltuieli obișnuite, de exemplu ulei sau filtre</span>
          </button>
          <button type="button" className={multi ? 'is-on' : ''} aria-pressed={multi} onClick={() => set({ amortizationType: 'multi', amortizationYears: String(Math.max(2, years)) })}>
            <strong>Pe mai mulți ani</strong><span>Pentru reparații mari, care țin câțiva ani</span>
          </button>
        </div>
      </div>
      {multi && (
        <div className="eq-stepper">
          <span>Număr de ani</span>
          <div>
            <button type="button" className="pm-icon-btn" aria-label="Mai puțini ani" disabled={years <= 2} onClick={() => set({ amortizationYears: String(years - 1) })}><Icon d={ICON.minus} size={18} /></button>
            <b>{years}</b>
            <button type="button" className="pm-icon-btn" aria-label="Mai mulți ani" disabled={years >= 10} onClick={() => set({ amortizationYears: String(years + 1) })}><Icon d={ICON.plus} size={18} /></button>
          </div>
        </div>
      )}
      {cost > 0 && (
        <p className="mo-tip">
          {multi ? `${lei(cost / years)} pe an, în ${repairYearsText(form)}` : `${lei(cost)}, în ${form.date?.slice(0, 4) || 'anul ales'}`}
        </p>
      )}
    </Panel>
  );
};

// ---------- Machine page ----------

const FuelFillsPanel = ({ equipment, fuel, year, onClose }) => (
  <Panel title={`Motorină ${year}`} subtitle={equipment.name} onClose={onClose}>
    {fuel.fills.length === 0 && <p className="pm-empty">Nicio alimentare în {year}.</p>}
    <div className="pm-list">
      {fuel.fills.map((fill) => (
        <div key={fill.id} className="pm-row eq-panel-row">
          <div className="pm-row-main">
            <strong>{formatDate(fill.date)}</strong>
            <span>{fill.fromTank === 'main' ? 'Din principal' : 'Din portabil'}{fill.driver ? ` · ${fill.driver}` : ''}</span>
          </div>
          <span className="pm-row-end">{litersText(fill.liters)}</span>
        </div>
      ))}
    </div>
  </Panel>
);

const EquipmentDetail = ({ equipment, year, fuelData, onClose, onEdit, onAddRepair, onOpenRepair, onDelete, onShowFuel }) => {
  const cost = calcAnnualCost(equipment, year);
  const fuel = fuelForEquipment(fuelData, equipment, year);
  const repairs = [...(equipment.repairs || [])].sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));

  return (
    <Panel title={equipment.name} subtitle={[equipment.type, equipment.brand, equipment.model, equipment.year].filter(Boolean).join(' · ')} onClose={onClose}
      footer={<button type="button" className="pm-btn-accent" onClick={onAddRepair}><Icon d={ICON.plus} size={18} />Adaugă cost de mentenanță</button>}>
      <div className="eq-tiles">
        <div><strong>{plainNumber(cost.total)}</strong><span>lei, total {year}</span></div>
        <div><strong>{plainNumber(cost.acquisition)}</strong><span>lei {equipment.acquisitionType === 'leasing' ? 'leasing' : 'achiziție'}</span></div>
        <div><strong>{plainNumber(cost.repairs)}</strong><span>lei mentenanță</span></div>
      </div>
      <p className="pm-muted">{costExplanation(equipment)}</p>
      {equipment.notes && <p className="pm-note">{equipment.notes}</p>}

      <button type="button" className="pm-row eq-panel-row" onClick={onShowFuel}>
        <span className="eq-icon"><Icon d="M5 21V5a2 2 0 012-2h6a2 2 0 012 2v16M3 21h14M8 8h4M15 12h2a2 2 0 012 2v3a1.5 1.5 0 003 0V9l-3-3" /></span>
        <div className="pm-row-main">
          <strong>Motorină în {year}</strong>
          <span>{fuel.fills.length} alimentări{fuel.fills[0] ? ` · ultima pe ${formatDate(fuel.fills[0].date)}` : ''}</span>
        </div>
        <span className="eq-end"><strong>{litersText(fuel.liters)}</strong><span>{lei(fuel.cost)} estimat</span></span>
      </button>

      <div className="pm-card-head">
        <h3>Reparații și mentenanță</h3>
        <span className="pm-muted">{repairs.length} {repairs.length === 1 ? 'înregistrare' : 'înregistrări'}</span>
      </div>
      {repairs.length === 0 && <p className="pm-muted">Nicio reparație încă.</p>}
      {repairs.map((repair) => (
        <button key={repair.id} type="button" className="pm-row eq-panel-row" onClick={() => onOpenRepair(repair)}>
          <div className="pm-row-main"><strong>{repair.description || 'Fără descriere'}</strong><span>{formatDate(repair.date)}</span></div>
          <span className="eq-end">
            <strong>{lei(repair.cost)}</strong>
            <span>{repair.amortizationType === 'multi' ? `${plainNumber(repairPerYear(repair))}/an, ${repair.amortizationYears} ani` : 'într-un an'}</span>
          </span>
        </button>
      ))}
      <div className="pm-two">
        <button type="button" className="pm-btn-soft" onClick={onEdit}><Icon d={ICON.edit} size={18} />Modifică</button>
        <button type="button" className="pm-btn-soft pm-danger" onClick={onDelete}><Icon d={ICON.trash} size={18} />Șterge</button>
      </div>
    </Panel>
  );
};

// ---------- Page ----------

const EquipmentManager = ({ dataService = universalDataService }) => {
  const [utilaje, setUtilaje] = useState([]);
  const [fuelData, setFuelData] = useState(() => normalizeFuel(null));
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [year, setYear] = useState(CURRENT_YEAR);
  const [detailId, setDetailId] = useState(null);
  const [equipmentForm, setEquipmentForm] = useState(null); // { equipment }
  const [repairForm, setRepairForm] = useState(null); // { repair }
  const [showFuel, setShowFuel] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const showToast = (message, type = 'success') => {
    clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), type === 'error' ? 6000 : 3000);
  };
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const load = useCallback(async () => {
    const [machines, fuel] = await Promise.all([dataService.loadUtilaje(), dataService.loadMotorina()]);
    setUtilaje(Array.isArray(machines) ? machines : []);
    setFuelData(normalizeFuel(fuel));
  }, [dataService]);

  useEffect(() => {
    load().catch((error) => console.error('Utilaje: load failed', error)).finally(() => setLoading(false));
    const reload = () => load().catch((error) => console.error('Utilaje: reload failed', error));
    window.addEventListener('cloudSyncComplete', reload);
    return () => window.removeEventListener('cloudSyncComplete', reload);
  }, [load]);

  // Starts from the newest saved copy, so a change from another device is not overwritten.
  const change = async (update, message) => {
    try {
      const saved = await dataService.loadUtilaje();
      const next = update(Array.isArray(saved) ? saved : []);
      await dataService.saveUtilaje(next);
      setUtilaje(next);
      showToast(message);
      return true;
    } catch (error) {
      console.error('Utilaje: save failed', error);
      showToast('Nu s-a salvat. Încearcă din nou.', 'error');
      return false;
    }
  };

  const costs = useMemo(() => utilaje.map((eq) => ({ eq, ...calcAnnualCost(eq, year), fuel: fuelForEquipment(fuelData, eq, year) })), [utilaje, fuelData, year]);
  const totals = costs.reduce((sum, row) => ({ total: sum.total + row.total, acquisition: sum.acquisition + row.acquisition, repairs: sum.repairs + row.repairs }), { total: 0, acquisition: 0, repairs: 0 });
  const detail = detailId ? utilaje.find((eq) => eq.id === detailId) : null;

  const saveEquipment = async (form) => {
    const { equipment } = equipmentForm;
    const saved = buildEquipment(form, equipment);
    const ok = await change((list) => (equipment ? list.map((eq) => (eq.id === equipment.id ? { ...saved, repairs: eq.repairs || [] } : eq)) : [...list, saved]),
      equipment ? 'Utilajul a fost modificat.' : 'Utilajul a fost salvat.');
    if (ok) setEquipmentForm(null);
  };

  const saveRepair = async (form) => {
    const { repair } = repairForm;
    const saved = buildRepair(form, repair?.id);
    const ok = await change((list) => list.map((eq) => {
      if (eq.id !== detailId) return eq;
      const repairs = repair ? (eq.repairs || []).map((item) => (item.id === repair.id ? saved : item)) : [...(eq.repairs || []), saved];
      return { ...eq, repairs };
    }), repair ? 'Costul a fost modificat.' : 'Costul a fost salvat.');
    if (ok) setRepairForm(null);
  };

  const askDeleteRepair = (repair) => setConfirm({
    title: 'Ștergi costul?',
    text: `${repair.description || 'Costul'}, ${lei(repair.cost)}. Ștergerea nu se poate anula.`,
    action: 'Șterge costul',
    onYes: async () => {
      setConfirm(null);
      const ok = await change((list) => list.map((eq) => (eq.id !== detailId ? eq : { ...eq, repairs: (eq.repairs || []).filter((item) => item.id !== repair.id) })), 'Costul a fost șters.');
      if (ok) setRepairForm(null);
    },
  });

  const askDeleteEquipment = (equipment) => setConfirm({
    title: `Ștergi ${equipment.name}?`,
    text: `Se șterg utilajul și cele ${(equipment.repairs || []).length} costuri ale lui. Ieșirile de motorină rămân în Motorină. Ștergerea nu se poate anula.`,
    action: 'Șterge utilajul',
    onYes: async () => {
      setConfirm(null);
      const ok = await change((list) => list.filter((eq) => eq.id !== equipment.id), 'Utilajul a fost șters.');
      if (ok) setDetailId(null);
    },
  });

  const paying = costs.filter((row) => row.total > 0);

  return (
    <div className="pm eq">
      <header className="pm-head">
        <h1>Utilaje</h1>
        <div className="pm-head-actions">
          <button type="button" className="pm-btn-accent pm-only-wide" onClick={() => setEquipmentForm({ equipment: null })}><Icon d={ICON.plus} size={18} />Utilaj</button>
          <select className="eq-year" aria-label="Anul" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {yearsFor(utilaje).map((option) => <option key={option} value={option}>Anul {option}</option>)}
          </select>
        </div>
      </header>

      {loading ? <p className="pm-empty">Se încarcă datele…</p> : (
        <>
          <section className="mo-hero">
            <p>Cost total în {year}</p>
            <strong className="mo-hero-big">{lei(totals.total)}</strong>
            <div className="eq-split">
              {totals.acquisition > 0 && <i style={{ flex: totals.acquisition }} />}
              {totals.repairs > 0 && <i style={{ flex: totals.repairs, minWidth: 14 }} />}
            </div>
            <div className="eq-split-text">
              <span>Achiziție și leasing {plainNumber(totals.acquisition)}</span>
              <span>Mentenanță {plainNumber(totals.repairs)}</span>
            </div>
          </section>

          <div className="pm-segments eq-segments" role="group" aria-label="Secțiuni">
            <button type="button" className={view === 'list' ? 'is-on' : ''} aria-pressed={view === 'list'} onClick={() => setView('list')}>Utilaje · {utilaje.length}</button>
            <button type="button" className={view === 'costs' ? 'is-on' : ''} aria-pressed={view === 'costs'} onClick={() => setView('costs')}>Costuri anuale</button>
          </div>

          {view === 'list' && (
            <div className="pm-list">
              {utilaje.length === 0 && <p className="pm-empty">Niciun utilaj încă. Adaugă primul utilaj.</p>}
              {costs.map(({ eq, total, repairs, fuel }) => (
                <button key={eq.id} type="button" className="pm-row" onClick={() => setDetailId(eq.id)}>
                  <div className="pm-row-main">
                    <strong>{eq.name}</strong>
                    <span>{acquisitionText(eq)} · {fuel.liters > 0 ? `motorină ${litersText(fuel.liters)}` : 'fără motorină'}</span>
                  </div>
                  <span className="eq-end">
                    <strong>{lei(total)}</strong>
                    <span>{repairs > 0 ? `${plainNumber(repairs)} mentenanță` : 'fără mentenanță'}</span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {view === 'costs' && (
            <div className="pm-list">
              {paying.length === 0 && <p className="pm-empty">Nicio cheltuială în {year}.</p>}
              {paying.map(({ eq, acquisition, repairs, total }) => (
                <button key={eq.id} type="button" className="pm-row" onClick={() => setDetailId(eq.id)}>
                  <div className="pm-row-main">
                    <strong>{eq.name}</strong>
                    <span>{eq.type} · {acquisitionText(eq)} {plainNumber(acquisition)} · mentenanță {plainNumber(repairs)}</span>
                  </div>
                  <span className="pm-row-end">{lei(total)}</span>
                </button>
              ))}
              {paying.length > 0 && <div className="mo-table-total"><span>Total {year}</span><span>{lei(totals.total)}</span></div>}
            </div>
          )}

          <button type="button" className="pm-fab st-fab" onClick={() => setEquipmentForm({ equipment: null })}><Icon d={ICON.plus} size={22} />Utilaj</button>
        </>
      )}

      {detail && (
        <EquipmentDetail equipment={detail} year={year} fuelData={fuelData} onClose={() => setDetailId(null)}
          onEdit={() => setEquipmentForm({ equipment: detail })} onAddRepair={() => setRepairForm({ repair: null })}
          onOpenRepair={(repair) => setRepairForm({ repair })} onDelete={() => askDeleteEquipment(detail)} onShowFuel={() => setShowFuel(true)} />
      )}
      {detail && showFuel && <FuelFillsPanel equipment={detail} fuel={fuelForEquipment(fuelData, detail, year)} year={year} onClose={() => setShowFuel(false)} />}
      {equipmentForm && <EquipmentPanel equipment={equipmentForm.equipment} onSave={saveEquipment} onClose={() => setEquipmentForm(null)} />}
      {detail && repairForm && (
        <RepairPanel equipment={detail} repair={repairForm.repair} onSave={saveRepair} onDelete={() => askDeleteRepair(repairForm.repair)} onClose={() => setRepairForm(null)} />
      )}
      {confirm && <ConfirmPanel {...confirm} onClose={() => setConfirm(null)} />}
      {toast && <div className={`pm-toast is-${toast.type}`} role="status">{toast.message}</div>}
    </div>
  );
};

export default EquipmentManager;
